import type { PrintJob, PrintJobServerTime } from '../types'
import { estimateServerNowMs as estimateServerNowFromSamples, firestoreServerMillis, medianNumber } from './serverClock'

export { firestoreServerMillis }

/**
 * How far a device clock may drift from the server before the job log calls it out.
 * Tablets with automatic time disabled have been seen 30+ minutes ahead.
 */
export const PRINT_JOB_CLOCK_SKEW_TOLERANCE_MS = 120_000

/**
 * Millis from Firestore's server clock.
 * Returns null for legacy docs written before `serverAt` existed.
 */
export const printJobServerMillis = firestoreServerMillis

/** Millis from the requesting device's own clock (shiftable — prefer `printJobTimeMillis`). */
export function printJobDeviceMillis(job: Pick<PrintJob, 'requestedAt'>): number | null {
  const ms = Date.parse(job.requestedAt ?? '')
  return Number.isFinite(ms) ? ms : null
}

/** Authoritative job time: server clock when present, device clock otherwise. */
export function printJobTimeMillis(job: Pick<PrintJob, 'requestedAt' | 'serverAt'>): number | null {
  return printJobServerMillis(job.serverAt) ?? printJobDeviceMillis(job)
}

/**
 * Signed device-clock offset in millis (positive = device ahead of the server).
 * Null when the two clocks are not comparable for this job.
 */
export function printJobClockSkewMs(
  job: Pick<PrintJob, 'requestedAt' | 'serverAt'>,
): number | null {
  const serverMs = printJobServerMillis(job.serverAt)
  const deviceMs = printJobDeviceMillis(job)
  if (serverMs === null || deviceMs === null) return null
  return deviceMs - serverMs
}

/** True when this job's requesting device is far enough off the server clock to be worth flagging. */
export function printJobHasClockSkew(job: Pick<PrintJob, 'requestedAt' | 'serverAt'>): boolean {
  const skew = printJobClockSkewMs(job)
  return skew !== null && Math.abs(skew) >= PRINT_JOB_CLOCK_SKEW_TOLERANCE_MS
}

/** Compact skew label for the job log badge, e.g. `31m` or `2h 5m`. */
export function formatClockSkew(ms: number): string {
  const totalMinutes = Math.round(Math.abs(ms) / 60_000)
  if (totalMinutes < 1) return '<1m'
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes}m`
  if (minutes === 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}

const LOCAL_CLOCK_OFFSET_SAMPLE_CAP = 5

function localClockOffsetSamples(jobs: readonly PrintJob[], localDeviceId: string): number[] {
  const local = localDeviceId.trim()
  if (!local) return []
  return jobs
    .filter((job) => (job.requestedBy ?? '').trim() === local)
    .map((job) => {
      const skew = printJobClockSkewMs(job)
      const serverMs = printJobServerMillis(job.serverAt)
      if (skew === null || serverMs === null) return null
      return { skew, serverMs }
    })
    .filter((s): s is { skew: number; serverMs: number } => s !== null)
    .sort((a, b) => b.serverMs - a.serverMs)
    .slice(0, LOCAL_CLOCK_OFFSET_SAMPLE_CAP)
    .map((s) => s.skew)
}

/**
 * Signed offset of this device's clock from the server, estimated from jobs this
 * device wrote (both timestamps present). Positive = device ahead. 0 when unknown.
 */
export function estimateLocalClockOffsetMs(
  jobs: readonly PrintJob[],
  localDeviceId: string,
): number {
  const samples = localClockOffsetSamples(jobs, localDeviceId)
  if (samples.length === 0) return 0
  return medianNumber(samples)
}

/**
 * Server "now" for claim TTL. Uses this tablet's clock offset when we have
 * samples; otherwise the latest `serverAt` on any job (so a skewed kitchen
 * tablet can still see a cashier note as live). Never `max` with wall clock.
 */
export function estimateServerNowMs(
  jobs: readonly PrintJob[],
  localDeviceId: string,
  nowMs: number = Date.now(),
): number {
  const samples = localClockOffsetSamples(jobs, localDeviceId)
  let latest: number | null = null
  for (const job of jobs) {
    const ms = printJobServerMillis(job.serverAt)
    if (ms === null) continue
    if (latest === null || ms > latest) latest = ms
  }
  return estimateServerNowFromSamples(samples, latest, nowMs)
}

/** Compact duration for hop timings, e.g. `180ms` or `8.0s`. */
export function formatDurationMs(ms: number): string {
  const abs = Math.abs(ms)
  if (abs < 1000) return `${Math.round(abs)}ms`
  const seconds = abs / 1000
  if (seconds < 10) return `${seconds.toFixed(1)}s`
  return `${Math.round(seconds)}s`
}

/**
 * Server-clock gap between two hops. Null when either clock is missing.
 */
export function printJobHopDurationMs(
  from: PrintJobServerTime | undefined,
  to: PrintJobServerTime | undefined,
): number | null {
  const a = printJobServerMillis(from)
  const b = printJobServerMillis(to)
  if (a === null || b === null) return null
  return b - a
}

/**
 * True when `job` is more recent than `other`, using the server clock where both have it.
 * Falls back to the device timestamp string so legacy docs still order sensibly.
 */
export function isNewerPrintJob(
  job: Pick<PrintJob, 'requestedAt' | 'serverAt'>,
  other: Pick<PrintJob, 'requestedAt' | 'serverAt'>,
): boolean {
  const a = printJobTimeMillis(job)
  const b = printJobTimeMillis(other)
  if (a !== null && b !== null) return a > b
  return (job.requestedAt ?? '') > (other.requestedAt ?? '')
}
